import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import Breadcrumb from "../../../../layout/breadcrumb";
import { Button, Card, CardBody, Col, Container, Input, Row, Table } from "reactstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import SweetAlert from "sweetalert2";
import PreLoader from "../../../../share-components/ui/preLoader";
import {
    downloadMappingSheetApi,
    getMetafieldsApi,
    saveMetafieldSourceApi,
    syncMetafieldsApi,
    syncToShopifyApi,
} from "../../../../services/manage-product/setting/categoryMetafieldMappingService";

const dataMenu = [
    { type: 0, text: "Products", link: "/product" },
    { type: 1, text: "Product Setting", link: "" },
    { type: 1, text: "Category Metafield Mapping", link: "" },
];

const norm = (s) => (s || "").toString().toLowerCase().replace(/[^a-z0-9]+/g, "");

const statusOptions = [
    { value: "all", label: "All metafields" },
    { value: "mapped", label: "Fully mapped" },
    { value: "unmapped", label: "Has unmapped" },
];

const CategoryMetafieldMapping = () => {
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [syncingMeta, setSyncingMeta] = useState(false);
    const [savingKey, setSavingKey] = useState(null);
    const [pushingKey, setPushingKey] = useState(null);
    const [metafields, setMetafields] = useState([]);
    const [sourceOptions, setSourceOptions] = useState([]);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState(statusOptions[0]);

    const loadData = async () => {
        setLoading(true);
        try {
            const { response } = await getMetafieldsApi();
            setMetafields(response.metafields || []);
            setSourceOptions(response.source_options || []);
        } catch (e) {
            console.error("Metafield mapping load failed", e);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredRows = useMemo(() => {
        const q = norm(search);
        const status = statusFilter?.value || "all";
        return metafields.filter((m) => {
            if (q && !norm(m.label).includes(q)) return false;
            const fullyMapped = m.our_total > 0 && m.mapped >= m.our_total;
            if (status === "mapped" && !fullyMapped) return false;
            if (status === "unmapped" && fullyMapped) return false;
            return true;
        });
    }, [metafields, search, statusFilter]);

    const handleSourceChange = async (row, options) => {
        // Multi-select: an array of chosen source attributes (or [] when cleared).
        const source = Array.isArray(options) ? options.map((o) => o.value) : [];
        setSavingKey(row.metafield_key);
        try {
            const { response } = await saveMetafieldSourceApi({
                metafield_key: row.metafield_key,
                source_attribute: source,
            });
            if (response.cleared_mappings) {
                toast.info(`Source changed — cleared ${response.cleared_mappings} old value mappings.`);
            } else {
                toast.success("Source updated.");
            }
            await loadData();
        } catch (e) {
            toast.error("Failed to update source");
        }
        setSavingKey(null);
    };

    const handleSyncMetafields = async () => {
        setSyncingMeta(true);
        try {
            const { response } = await syncMetafieldsApi();
            if (response.added) {
                toast.success(`Added ${response.added} new category metafield(s).`);
            } else {
                toast.info("No new category metafields to sync.");
            }
            await loadData();
        } catch (e) {
            toast.error(e?.error || "Failed to sync category metafields");
        }
        setSyncingMeta(false);
    };

    const handleDownloadSheet = async () => {
        setDownloading(true);
        try {
            // Streamed as a blob (?download=1) and saved directly — same as the other
            // export downloads in the app. No new tab, no cross-host URL.
            const { response } = await downloadMappingSheetApi();
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `shopify-metafield-mapping-sheet-${new Date().toISOString().slice(0, 10)}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Failed to download mapping sheet");
        }
        setDownloading(false);
    };

    const handleSyncToShopify = async (m) => {
        setPushingKey(m.metafield_key);
        try {
            // Dry run to preview counts, then confirm before writing.
            const { response: dry } = await syncToShopifyApi(m.metafield_key, false);
            const html = `This will create/update <b>${dry.written}</b> entr${dry.written === 1 ? "y" : "ies"} in Shopify for "${m.label}".`
                + (dry.skipped ? `<br/><span class="text-muted">${dry.skipped} value(s) skipped — missing a required base.</span>` : "");
            const result = await SweetAlert.fire({
                title: "Sync to Shopify",
                html,
                icon: "warning",
                showCancelButton: true,
                animation: false,
                confirmButtonText: "Yes, sync",
                cancelButtonText: "Cancel",
                reverseButtons: true,
            });
            if (!result.isConfirmed) {
                setPushingKey(null);
                return;
            }
            const { response } = await syncToShopifyApi(m.metafield_key, true);
            if (response.failed) {
                toast.error(`Synced ${response.written}; ${response.failed} failed. Please try again later.`);
                console.warn("Sync to Shopify errors:", response.errors);
            } else {
                toast.success(`${response.written} value${response.written === 1 ? "" : "s"} synced to Shopify successfully.`);
            }
            await loadData(); // refresh the "Last synced" stamp
        } catch (e) {
            toast.error(e?.error || "Sync to Shopify failed");
        }
        setPushingKey(null);
    };

    const openDetail = (row) => {
        history.push(`${process.env.PUBLIC_URL}/product/setting/category-metafield-mapping/${row.metafield_key}`);
    };

    const renderRow = (m) => {
        const isConfigurable = m.kind === "standard" || m.kind === "special";
        const sources = Array.isArray(m.source_attribute) ? m.source_attribute : (m.source_attribute ? [m.source_attribute] : []);
        const selected = sourceOptions.filter((o) => sources.includes(o.value));
        const hasSource = sources.length > 0;
        return (
            <tr key={m.metafield_key}>
                <td style={{ verticalAlign: "middle", fontWeight: 600, width: "20%" }}>
                    {m.label}
                    {m.kind === "special" && <span className="badge badge-light ml-2">special</span>}
                </td>
                <td style={{ width: "28%" }}>
                    {isConfigurable ? (
                        <Select
                            isMulti
                            isClearable
                            options={sourceOptions}
                            value={selected}
                            isDisabled={savingKey === m.metafield_key}
                            isLoading={savingKey === m.metafield_key}
                            placeholder="Choose our attribute(s)…"
                            onChange={(opts) => handleSourceChange(m, opts)}
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                    ) : m.kind === "derived" ? (
                        <span className="text-muted">Derived: <b>{m.derived_value}</b></span>
                    ) : (
                        <span className="text-muted">— no marketplace source —</span>
                    )}
                </td>
                <td style={{ verticalAlign: "middle", width: "16%" }}>
                    {isConfigurable ? (
                        <Fragment>
                            <span className={m.mapped >= m.our_total && m.our_total > 0 ? "text-success" : "text-dark"}>
                                <b>{m.mapped}</b> / {m.our_total} mapped
                            </span>
                            <small className="text-muted d-block">Shopify values synced: {m.taxonomy_synced}</small>
                        </Fragment>
                    ) : m.kind === "derived" ? (
                        <span className={m.derived_synced ? "text-success" : "text-muted"}>
                            {m.derived_synced ? "Synced" : "Not synced"}
                        </span>
                    ) : (
                        <span className="text-muted">n/a</span>
                    )}
                </td>
                <td style={{ verticalAlign: "middle", width: "16%" }}>
                    {!(isConfigurable || m.kind === "derived") ? (
                        <span className="text-muted">—</span>
                    ) : m.last_synced_at ? (
                        <Fragment>
                            <span style={{ fontSize: 12 }}>{m.last_synced_at}</span>
                            {m.last_synced_by && (
                                <small className="text-muted d-block">by {m.last_synced_by}</small>
                            )}
                        </Fragment>
                    ) : (
                        <span className="text-muted" style={{ fontSize: 12 }}>Never synced</span>
                    )}
                </td>
                <td style={{ verticalAlign: "middle", width: "20%" }} className="text-right">
                    {isConfigurable && (
                        <Fragment>
                            {m.mapped > 0 && (
                                <Button size="sm" color="success" className="mr-2 mb-1" disabled={pushingKey === m.metafield_key} onClick={() => handleSyncToShopify(m)}>
                                    {pushingKey === m.metafield_key
                                        ? <Fragment><i className="fa fa-spinner fa-spin mr-1" aria-hidden="true" />Syncing&hellip;</Fragment>
                                        : "Sync to Shopify"}
                                </Button>
                            )}
                            <Button size="sm" color="primary" outline className="mb-1" disabled={!hasSource} onClick={() => openDetail(m)}>
                                Map values →
                            </Button>
                        </Fragment>
                    )}
                    {m.kind === "derived" && (
                        <Button size="sm" color="success" className="mb-1" disabled={pushingKey === m.metafield_key} onClick={() => handleSyncToShopify(m)}>
                            {pushingKey === m.metafield_key
                                ? <Fragment><i className="fa fa-spinner fa-spin mr-1" aria-hidden="true" />Syncing&hellip;</Fragment>
                                : "Sync to Shopify"}
                        </Button>
                    )}
                </td>
            </tr>
        );
    };

    return (
        <Fragment>
            <Container fluid={true} className="product-wrapper">
                <Breadcrumb data={dataMenu} title="Category Metafield Mapping" />
                <div className="product-grid">
                    <div className="feature-products">
                        <Card>
                            <Row className="p-3 align-items-center">
                                <Col md="7">
                                    <h5 className="mb-1">Map our attribute values to Shopify category metafields</h5>
                                    <small className="text-muted">
                                        Pick which of our attributes feeds each Shopify metafield, then open a metafield to map its values.
                                    </small>
                                </Col>
                                <Col md="5">
                                    <div className="float-md-right mt-2 mt-md-0">
                                        <Button size="sm" color="primary" outline className="mr-2" disabled={syncingMeta} onClick={handleSyncMetafields}>
                                            {syncingMeta
                                                ? <Fragment><i className="fa fa-spinner fa-spin mr-1" aria-hidden="true" />Syncing&hellip;</Fragment>
                                                : "Sync Shopify Category Metafields"}
                                        </Button>
                                        <Button size="sm" color="secondary" outline disabled={downloading} onClick={handleDownloadSheet}>
                                            {downloading
                                                ? <Fragment><i className="fa fa-spinner fa-spin mr-1" aria-hidden="true" />Preparing&hellip;</Fragment>
                                                : "Download Mapping Sheet"}
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </Card>

                        <Card>
                            <CardBody>
                                <Row className="mb-1">
                                    <Col md="6" className="mb-2">
                                        <Input
                                            type="text"
                                            placeholder="Search metafield…"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                        />
                                    </Col>
                                    <Col md="4" className="mb-2">
                                        <Select
                                            options={statusOptions}
                                            value={statusFilter}
                                            onChange={(opt) => setStatusFilter(opt || statusOptions[0])}
                                        />
                                    </Col>
                                </Row>

                                {loading ? (
                                    <PreLoader />
                                ) : (
                                    <div className="table-responsive product-table mt-2">
                                        <Table className="mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Shopify Category Metafield</th>
                                                    <th>Maps From (our attribute)</th>
                                                    <th>Status</th>
                                                    <th>Last synced</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredRows.map(renderRow)}
                                                {!filteredRows.length && (
                                                    <tr>
                                                        <td colSpan="5" className="text-center text-muted py-4">
                                                            {metafields.length ? "No metafields match your filters." : "No metafields found."}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </Container>
        </Fragment>
    );
};

export default CategoryMetafieldMapping;
